/**
 * 鸿蒙应用锁（B7）：开启后启动/回前台需通过系统生物识别/锁屏密码验证
 * （ArkTS 桥命令 user_auth，UserAuthenticationKit：PIN/面容/指纹，ATL2）
 *
 * - 开关持久化 localStorage（harmony_app_lock_enabled），仅鸿蒙平台生效
 * - 锁定时渲染全屏遮罩（最高层级），阻断应用交互
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { invoke } from '@tauri-apps/api/core'
import { isHarmonyOS } from '../utils/mobile'

export const APP_LOCK_STORAGE_KEY = 'harmony_app_lock_enabled'

export function isHarmonyAppLockEnabled(): boolean {
  if (!isHarmonyOS()) return false
  return localStorage.getItem(APP_LOCK_STORAGE_KEY) === '1'
}

/** 设置开关（仅鸿蒙可开；开启前先做一次认证验证设备已录入凭据） */
export async function setHarmonyAppLockEnabled(enabled: boolean): Promise<void> {
  if (enabled) {
    // 唤起一次系统认证：确认设备已录入锁屏密码/生物识别，且用户知情
    await invoke('user_auth', { title: '开启应用锁' })
    localStorage.setItem(APP_LOCK_STORAGE_KEY, '1')
    return
  }
  localStorage.removeItem(APP_LOCK_STORAGE_KEY)
}

export default function HarmonyAppLockGate() {
  // 非鸿蒙或未开启：恒不渲染（useState 初始化仅执行一次）
  const [locked, setLocked] = useState(() => isHarmonyAppLockEnabled())
  const [message, setMessage] = useState('')
  const [authenticating, setAuthenticating] = useState(false)
  const authInFlightRef = useRef(false)

  const authenticate = useCallback(async () => {
    if (authInFlightRef.current) return
    authInFlightRef.current = true
    setAuthenticating(true)
    setMessage('')
    try {
      await invoke('user_auth', { title: '解锁灵犀笔记' })
      setLocked(false)
    } catch (e) {
      // 取消/失败：保持锁定，展示原因与重试按钮
      setMessage(e instanceof Error ? e.message : String(e))
    } finally {
      authInFlightRef.current = false
      setAuthenticating(false)
    }
  }, [])

  // 挂载即认证（冷启动锁）
  useEffect(() => {
    if (isHarmonyAppLockEnabled()) {
      authenticate()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 回前台重新锁定（WebView visibilitychange 在应用前后台切换时触发）
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return
      if (!isHarmonyAppLockEnabled()) return
      setLocked(true)
      authenticate()
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [authenticate])

  if (!locked) return null

  return (
    <div className="fixed inset-0 z-[99999] bg-white dark:bg-gray-900 flex flex-col items-center justify-center px-8 select-none">
      <svg className="w-16 h-16 text-gray-400 dark:text-gray-500 mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
      </svg>
      <p className="text-lg font-medium text-gray-800 dark:text-gray-100 mb-2">应用已锁定</p>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-8 text-center">
        {message || '使用指纹、面容或锁屏密码解锁'}
      </p>
      <button
        className="px-8 py-3 rounded-lg text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 transition-colors text-sm font-medium"
        disabled={authenticating}
        onClick={() => authenticate()}
      >
        {authenticating ? '验证中…' : '解锁'}
      </button>
    </div>
  )
}
